import { useState } from "react";
import { motion } from "framer-motion";
import { Send, Loader2 } from "lucide-react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const revenueOptions = [
  "Under $10,000",
  "$10,000 - $50,000",
  "$50,000 - $100,000",
  "$100,000 - $500,000",
  "$500,000 - $1M",
  "$1M+",
];

const budgetOptions = [
  "Under $1,000",
  "$1,000 - $3,000",
  "$3,000 - $5,000",
  "$5,000 - $10,000",
  "$10,000+",
];

const heardOptions = [
  "Instagram",
  "Facebook",
  "TikTok",
  "LinkedIn",
  "Google Search",
  "Referral",
  "Other",
];

export default function ContactForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    website_url: "",
    monthly_revenue: "",
    marketing_budget: "",
    how_heard: "",
    message: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.first_name || !formData.last_name || !formData.email || 
        !formData.phone || !formData.monthly_revenue || !formData.marketing_budget || 
        !formData.how_heard || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Save to database
      await axios.post(`${API}/contact`, formData);
      
      // Send email via Formsubmit.co
      const formSubmitData = new FormData();
      formSubmitData.append("name", `${formData.first_name} ${formData.last_name}`);
      formSubmitData.append("email", formData.email);
      formSubmitData.append("phone", formData.phone);
      formSubmitData.append("website", formData.website_url || "Not provided");
      formSubmitData.append("monthly_revenue", formData.monthly_revenue);
      formSubmitData.append("marketing_budget", formData.marketing_budget);
      formSubmitData.append("how_heard", formData.how_heard);
      formSubmitData.append("message", formData.message);
      formSubmitData.append("_subject", `New Lead: ${formData.first_name} ${formData.last_name} - TAKEOFF`);
      formSubmitData.append("_template", "table");
      
      await fetch("https://formsubmit.co/ajax/82c0fcd1131312ed96d538d6fc106690", {
        method: "POST",
        body: formSubmitData,
      });
      
      // Fire Meta Pixel Lead event
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'Lead', {
          content_name: 'Contact Form Submission',
          content_category: formData.how_heard,
          value: formData.marketing_budget,
          currency: 'USD'
        });
      }
      
      // Fire Google Analytics & Google Ads events
      if (typeof window.gtag === 'function') {
        // GA4 event
        window.gtag('event', 'generate_lead', {
          event_category: 'Contact',
          event_label: formData.how_heard,
          value: formData.marketing_budget
        });
        // Google Ads conversion
        window.gtag('event', 'conversion', {
          send_to: 'AW-16917911601/LEAD',
          value: 1.0,
          currency: 'USD'
        });
      }
      
      toast.success("Message sent successfully! We'll be in touch soon.");
      setFormData({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        website_url: "",
        monthly_revenue: "",
        marketing_budget: "",
        how_heard: "",
        message: "",
      });
    } catch (error) {
      console.error("Form submission error:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      data-testid="contact-section"
      id="contact"
      className="py-24 md:py-32 bg-dark-secondary"
    >
      <div className="max-w-4xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2
            data-testid="contact-title"
            className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl uppercase text-white"
          >
            Ready to Scale? <span className="text-lime">Reach Out</span>
          </h2>
          <p className="font-body text-white/60 mt-4">
            Fill out the form below and we'll get back to you within 24 hours.
          </p>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          onSubmit={handleSubmit}
          data-testid="contact-form"
          className="space-y-6"
        >
          {/* Name Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="font-body text-sm text-white/70 mb-2 block">
                First Name *
              </label>
              <Input
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                data-testid="input-first-name"
                placeholder="John"
                className="bg-dark border-white/10 text-white placeholder:text-white/30 focus:border-lime h-12"
              />
            </div>
            <div>
              <label className="font-body text-sm text-white/70 mb-2 block">
                Last Name *
              </label>
              <Input
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                data-testid="input-last-name"
                placeholder="Doe"
                className="bg-dark border-white/10 text-white placeholder:text-white/30 focus:border-lime h-12"
              />
            </div>
          </div>

          {/* Email & Phone */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="font-body text-sm text-white/70 mb-2 block">
                Email *
              </label>
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                data-testid="input-email"
                placeholder="john@company.com"
                className="bg-dark border-white/10 text-white placeholder:text-white/30 focus:border-lime h-12"
              />
            </div>
            <div>
              <label className="font-body text-sm text-white/70 mb-2 block">
                Phone *
              </label>
              <Input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                data-testid="input-phone"
                placeholder="(514) 123-4567"
                className="bg-dark border-white/10 text-white placeholder:text-white/30 focus:border-lime h-12"
              />
            </div>
          </div>

          {/* Website */}
          <div>
            <label className="font-body text-sm text-white/70 mb-2 block">
              Website URL (optional)
            </label>
            <Input
              type="url"
              name="website_url"
              value={formData.website_url}
              onChange={handleInputChange}
              data-testid="input-website"
              placeholder="https://yourcompany.com"
              className="bg-dark border-white/10 text-white placeholder:text-white/30 focus:border-lime h-12"
            />
          </div>

          {/* Dropdowns Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="font-body text-sm text-white/70 mb-2 block">
                Average Monthly Revenue *
              </label>
              <Select
                value={formData.monthly_revenue}
                onValueChange={(value) =>
                  handleSelectChange("monthly_revenue", value)
                }
              >
                <SelectTrigger
                  data-testid="select-revenue"
                  className="bg-dark border-white/10 text-white h-12 focus:ring-lime"
                >
                  <SelectValue placeholder="Select revenue range" />
                </SelectTrigger>
                <SelectContent className="bg-dark-card border-white/10">
                  {revenueOptions.map((option) => (
                    <SelectItem
                      key={option}
                      value={option}
                      className="text-white hover:bg-lime hover:text-black focus:bg-lime focus:text-black"
                    >
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="font-body text-sm text-white/70 mb-2 block">
                Monthly Marketing Budget *
              </label>
              <Select
                value={formData.marketing_budget}
                onValueChange={(value) =>
                  handleSelectChange("marketing_budget", value)
                }
              >
                <SelectTrigger
                  data-testid="select-budget"
                  className="bg-dark border-white/10 text-white h-12 focus:ring-lime"
                >
                  <SelectValue placeholder="Select budget range" />
                </SelectTrigger>
                <SelectContent className="bg-dark-card border-white/10">
                  {budgetOptions.map((option) => (
                    <SelectItem
                      key={option}
                      value={option}
                      className="text-white hover:bg-lime hover:text-black focus:bg-lime focus:text-black"
                    >
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* How did you hear */}
          <div>
            <label className="font-body text-sm text-white/70 mb-2 block">
              How did you hear about us? *
            </label>
            <Select
              value={formData.how_heard}
              onValueChange={(value) => handleSelectChange("how_heard", value)}
            >
              <SelectTrigger
                data-testid="select-heard"
                className="bg-dark border-white/10 text-white h-12 focus:ring-lime"
              >
                <SelectValue placeholder="Select an option" />
              </SelectTrigger>
              <SelectContent className="bg-dark-card border-white/10">
                {heardOptions.map((option) => (
                  <SelectItem
                    key={option}
                    value={option}
                    className="text-white hover:bg-lime hover:text-black focus:bg-lime focus:text-black"
                  >
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Message */}
          <div>
            <label className="font-body text-sm text-white/70 mb-2 block">
              How can we help? *
            </label>
            <Textarea
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              data-testid="input-message"
              placeholder="Tell us about your business and what you're looking to achieve..."
              rows={5}
              className="bg-dark border-white/10 text-white placeholder:text-white/30 focus:border-lime resize-none"
            />
          </div>

          {/* Submit Button */}
          <div className="pt-4">
            <button
              type="submit"
              disabled={isSubmitting}
              data-testid="submit-btn"
              className="w-full md:w-auto inline-flex items-center justify-center gap-3 bg-lime text-black font-body font-semibold text-base px-10 py-4 hover:bg-lime-hover transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  SENDING...
                </>
              ) : (
                <>
                  SEND
                  <Send className="w-5 h-5" />
                </>
              )}
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
}
